import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './CompteRenduDetail.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const CompteRenduDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [compteRendu, setCompteRendu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [envoi, setEnvoi] = useState({ loading: false, resultat: null });
  const [generationPDF, setGenerationPDF] = useState({ loading: false, error: null });

  useEffect(() => {
    chargerCompteRendu();
  }, [id]);

  const chargerCompteRendu = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/comptes-rendus/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompteRendu(response.data.data);
    } catch (err) {
      console.error('Erreur chargement:', err);
      setError('Impossible de charger le compte rendu');
    } finally {
      setLoading(false);
    }
  };

  const getStatutBadge = (statut) => {
    const badges = {
      brouillon: { class: 'badge-draft', text: 'BROUILLON' },
      en_attente: { class: 'badge-pending', text: 'EN ATTENTE' },
      valide: { class: 'badge-valid', text: 'VALIDÉ' },
      archive: { class: 'badge-archive', text: 'ARCHIVÉ' }
    };
    const badge = badges[statut] || badges.brouillon;
    return <span className={`status-badge ${badge.class}`}>{badge.text}</span>;
  };

  const formaterDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) return <div className="loading">Chargement...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!compteRendu) return <div className="error">Compte rendu introuvable</div>;

  const cr = compteRendu;
  
  // Helper pour parser les champs JSON
  const parseJSON = (data, fallback = []) => {
    if (!data) return fallback;
    if (Array.isArray(data)) return data;
    try {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      return Array.isArray(parsed) ? parsed : fallback;
    } catch (e) {
      console.error('Erreur parsing JSON:', e);
      return fallback;
    }
  };

  const envoyerParWhatsApp = async () => {
    if (!window.confirm(
      `Êtes-vous sûr de vouloir envoyer ce compte rendu par WhatsApp à tous les membres actifs ?\n\n` +
      `Cette action enverra le PDF à tous les membres ayant un numéro de téléphone enregistré.`
    )) {
      return;
    }

    try {
      setEnvoi({ loading: true, resultat: null });
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/comptes-rendus/${id}/envoyer-whatsapp`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setEnvoi({ loading: false, resultat: response.data.data });
      
      alert(
        `✅ Envoi terminé !\n\n` +
        `✓ Succès : ${response.data.data.succes}/${response.data.data.total}\n` +
        `${response.data.data.echecs > 0 ? `✗ Échecs : ${response.data.data.echecs}` : ''}`
      );

    } catch (err) {
      console.error('Erreur envoi WhatsApp:', err);
      setEnvoi({ loading: false, resultat: null });
      alert('❌ Erreur lors de l\'envoi : ' + (err.response?.data?.message || err.message));
    }
  };

  const genererPDF = async () => {
    if (!window.confirm(
      `Générer le PDF de ce compte rendu ?\n\n` +
      `${cr.pdf_filename ? 'Le PDF existant sera remplacé.' : 'Un nouveau PDF sera créé.'}`
    )) {
      return;
    }

    try {
      setGenerationPDF({ loading: true, error: null });
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/comptes-rendus/${id}/generer-pdf`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setGenerationPDF({ loading: false, error: null });
      
      // Recharger le compte rendu pour voir le nouveau PDF
      await chargerCompteRendu();
      
      alert(
        `✅ PDF généré avec succès !\n\n` +
        `Fichier : ${response.data.data.pdf_filename}`
      );

    } catch (err) {
      console.error('Erreur génération PDF:', err);
      setGenerationPDF({ loading: false, error: err.response?.data?.message || err.message });
      alert('❌ Erreur lors de la génération : ' + (err.response?.data?.message || err.message));
    }
  };

  return (
    <div className="compte-rendu-detail">
      <div className="detail-header">
        <button onClick={() => navigate('/admin/comptes-rendus')} className="btn-back">
          ← Retour
        </button>
        <h1>Compte Rendu de Réunion</h1>
        {getStatutBadge(cr.statut)}
      </div>

      <div className="detail-content">
        <section className="section-info">
          <h2>📍 Informations Générales</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Date:</label>
              <span>{formaterDate(cr.date_reunion)}</span>
            </div>
            <div className="info-item">
              <label>Lieu:</label>
              <span>{cr.lieu}</span>
            </div>
            <div className="info-item">
              <label>Heure prévue:</label>
              <span>{cr.heure_prevue}</span>
            </div>
            <div className="info-item">
              <label>Heure début:</label>
              <span>{cr.heure_debut_reelle || '-'}</span>
            </div>
            <div className="info-item">
              <label>Président de séance:</label>
              <span>{cr.president_seance || '-'}</span>
            </div>
            <div className="info-item">
              <label>Prière par:</label>
              <span>{cr.priere_par || '-'}</span>
            </div>
          </div>
        </section>

        {/* PARTICIPANTS */}
        {cr.participants && (() => {
          try {
            const participants = typeof cr.participants === 'string'
              ? JSON.parse(cr.participants)
              : cr.participants;

            if (!Array.isArray(participants) || participants.length === 0) return null;

            // Normaliser : present peut être boolean true/false OU entier 1/0
            const normalized = participants.map(p => ({
              ...p,
              nom: p.nom || p.nom_complet || '',
              estPresent: p.present === true || p.present === 1
            }));

            const nbPresents = normalized.filter(p => p.estPresent).length;
            const nbAbsents  = normalized.filter(p => !p.estPresent).length;

            return (
              <section className="section-participants">
                <h2>
                  👥 Participants &nbsp;
                  <span style={{ color: '#28a745' }}>{nbPresents} présent{nbPresents > 1 ? 's' : ''}</span>
                  {nbAbsents > 0 && (
                    <span style={{ color: '#dc3545', marginLeft: 8 }}>
                      / {nbAbsents} absent{nbAbsents > 1 ? 's' : ''}
                    </span>
                  )}
                </h2>

                {/* Présents */}
                {nbPresents > 0 && (
                  <>
                    <p style={{ fontWeight: 'bold', marginBottom: 8, color: '#155724' }}>Présents :</p>
                    <div className="participants-list" style={{ marginBottom: 16 }}>
                      {normalized.filter(p => p.estPresent).map((p, i) => (
                        <div key={i} className="participant-badge present">
                          {p.nom} ✓
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Absents */}
                {nbAbsents > 0 && (
                  <>
                    <p style={{ fontWeight: 'bold', marginBottom: 8, color: '#721c24' }}>Absents :</p>
                    <div className="participants-list">
                      {normalized.filter(p => !p.estPresent).map((p, i) => (
                        <div key={i} className="participant-badge absent">
                          {p.nom} ✗
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </section>
            );
          } catch (e) {
            console.error('Erreur parsing participants:', e);
            return <p>Erreur d\'affichage des participants</p>;
          }
        })()}

        {/* ✅ ANNONCES - CORRIGÉ avec parseJSON */}
        {parseJSON(cr.annonces).length > 0 && (
          <section className="section-annonces">
            <h2>📢 Annonces</h2>
            {parseJSON(cr.annonces).map((annonce, i) => (
              <div key={i} className="annonce-item">
                <strong>{annonce.membre}:</strong> {annonce.message}
              </div>
            ))}
          </section>
        )}

        {/* ✅ POINTS ORDRE DU JOUR - CORRIGÉ avec parseJSON */}
        {parseJSON(cr.points_ordre_jour).length > 0 && (
          <section className="section-points">
            <h2>📋 Points à l'Ordre du Jour</h2>
            {parseJSON(cr.points_ordre_jour).map((point, i) => (
              <div key={i} className="point-item">
                <h3>Point {point.numero}: {point.titre_fr}</h3>
                {point.titre_local && <p className="titre-local">{point.titre_local}</p>}
                <p>{point.contenu}</p>
              </div>
            ))}
          </section>
        )}

        {/* ✅ COTISATIONS - CORRIGÉ avec parseJSON */}
        {parseJSON(cr.cotisations).length > 0 && (() => {
          const toutes = parseJSON(cr.cotisations);
          // Anciens CR (avant la fusion tontine, migration 011) : ont encore "plats"
          // (caisse nourriture, obsolète) et pas de tontine_hote_nom. Nouveaux CR :
          // ont "tontine_hote"/"tontine_co_hote". On détecte le format pour ne pas
          // faire disparaître la donnée "Plats" des anciens comptes-rendus.
          const estLegacy = !cr.tontine_hote_nom && toutes.some(c => c.plats !== undefined);
          const aCoHote = !!cr.tontine_co_hote_nom;
          const num = (v) => Number(v) || 0;

          // Deux tableaux séparés (au lieu d'un seul avec toutes les colonnes) :
          // le tableau fusionné devenait trop étroit pour les noms longs dès qu'on
          // ajoute Deuil/Excellence/Adhésion en plus de la Tontine — colonne Membre
          // écrasée, texte qui se chevauche.
          const cotisationsRows = estLegacy
            ? toutes.filter(c => num(c.caisse) + num(c.maladie) + num(c.plats) > 0)
            : toutes.filter(c => num(c.caisse) + num(c.maladie) + num(c.deuil) + num(c.excellence) + num(c.adhesion) > 0);
          const tontineRows = toutes.filter(c => num(c.tontine_hote) + num(c.tontine_co_hote) > 0);

          return (
          <>
            <section className="section-cotisations">
              <h2>💰 Cotisations</h2>
              <table className="cotisations-table">
                <thead>
                  <tr>
                    <th>Membre</th>
                    <th>Caisse</th>
                    <th>Maladie</th>
                    {estLegacy ? (
                      <th>Plats</th>
                    ) : (
                      <>
                        <th>Deuil</th>
                        <th>Excellence scolaire</th>
                        <th>Adhésion</th>
                      </>
                    )}
                    <th>Sous-total</th>
                  </tr>
                </thead>
                <tbody>
                  {cotisationsRows.map((cot, i) => {
                    const sousTotal = estLegacy
                      ? num(cot.caisse) + num(cot.maladie) + num(cot.plats)
                      : num(cot.caisse) + num(cot.maladie) + num(cot.deuil) + num(cot.excellence) + num(cot.adhesion);
                    return (
                      <tr key={i}>
                        <td>{cot.nom}</td>
                        <td>{num(cot.caisse).toLocaleString('fr-FR')} FCFA</td>
                        <td>{num(cot.maladie).toLocaleString('fr-FR')} FCFA</td>
                        {estLegacy ? (
                          <td>{num(cot.plats).toLocaleString('fr-FR')} FCFA</td>
                        ) : (
                          <>
                            <td>{num(cot.deuil).toLocaleString('fr-FR')} FCFA</td>
                            <td>{num(cot.excellence).toLocaleString('fr-FR')} FCFA</td>
                            <td>{num(cot.adhesion).toLocaleString('fr-FR')} FCFA</td>
                          </>
                        )}
                        <td><strong>{sousTotal.toLocaleString('fr-FR')} FCFA</strong></td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td><strong>TOTAL</strong></td>
                    <td><strong>{num(cr.total_cotisations_caisse).toLocaleString('fr-FR')} FCFA</strong></td>
                    <td><strong>{num(cr.total_cotisations_maladie).toLocaleString('fr-FR')} FCFA</strong></td>
                    {estLegacy ? (
                      <td><strong>{num(cr.total_cotisations_plats).toLocaleString('fr-FR')} FCFA</strong></td>
                    ) : (
                      <>
                        <td><strong>{cotisationsRows.reduce((s, c) => s + num(c.deuil), 0).toLocaleString('fr-FR')} FCFA</strong></td>
                        <td><strong>{cotisationsRows.reduce((s, c) => s + num(c.excellence), 0).toLocaleString('fr-FR')} FCFA</strong></td>
                        <td><strong>{cotisationsRows.reduce((s, c) => s + num(c.adhesion), 0).toLocaleString('fr-FR')} FCFA</strong></td>
                      </>
                    )}
                    <td><strong>{cotisationsRows.reduce((s, c) => s + (estLegacy
                      ? num(c.caisse) + num(c.maladie) + num(c.plats)
                      : num(c.caisse) + num(c.maladie) + num(c.deuil) + num(c.excellence) + num(c.adhesion)), 0).toLocaleString('fr-FR')} FCFA</strong></td>
                  </tr>
                </tfoot>
              </table>
            </section>

            {!estLegacy && tontineRows.length > 0 && (
              <section className="section-tontine">
                <h2>🔄 Tontine</h2>
                <table className="cotisations-table">
                  <thead>
                    <tr>
                      <th>Membre</th>
                      <th>Tontine{cr.tontine_hote_nom ? ` (${cr.tontine_hote_nom})` : ''}</th>
                      {aCoHote && <th>Tontine ({cr.tontine_co_hote_nom})</th>}
                      <th>Sous-total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tontineRows.map((cot, i) => (
                      <tr key={i}>
                        <td>{cot.nom}</td>
                        <td>{num(cot.tontine_hote).toLocaleString('fr-FR')} FCFA</td>
                        {aCoHote && <td>{num(cot.tontine_co_hote).toLocaleString('fr-FR')} FCFA</td>}
                        <td><strong>{(num(cot.tontine_hote) + num(cot.tontine_co_hote)).toLocaleString('fr-FR')} FCFA</strong></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td><strong>TOTAL</strong></td>
                      <td><strong>{tontineRows.reduce((s, c) => s + num(c.tontine_hote), 0).toLocaleString('fr-FR')} FCFA</strong></td>
                      {aCoHote && (
                        <td><strong>{tontineRows.reduce((s, c) => s + num(c.tontine_co_hote), 0).toLocaleString('fr-FR')} FCFA</strong></td>
                      )}
                      <td><strong>{tontineRows.reduce((s, c) => s + num(c.tontine_hote) + num(c.tontine_co_hote), 0).toLocaleString('fr-FR')} FCFA</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </section>
            )}
          </>
          );
        })()}

        {/* ÉTAT DE CAISSE DU MOIS */}
        {parseJSON(cr.etat_caisse).length > 0 && (
          <section className="section-etat-caisse">
            <h2>📊 État de Caisse du Mois</h2>
            <table className="cotisations-table">
              <thead>
                <tr>
                  <th>Mois</th>
                  <th>Entrées</th>
                  <th>Sorties</th>
                  <th>Commentaires</th>
                </tr>
              </thead>
              <tbody>
                {parseJSON(cr.etat_caisse).map((l, i) => (
                  <tr key={i}>
                    <td>{l.mois}</td>
                    <td style={{ color: 'green' }}>{Number(l.entrees || 0).toLocaleString('fr-FR')} FCFA</td>
                    <td style={{ color: 'red' }}>{Number(l.sorties || 0).toLocaleString('fr-FR')} FCFA</td>
                    <td>{l.commentaire || '-'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td><strong>TOTAL</strong></td>
                  <td style={{ color: 'green' }}><strong>{parseJSON(cr.etat_caisse).reduce((s, l) => s + Number(l.entrees || 0), 0).toLocaleString('fr-FR')} FCFA</strong></td>
                  <td style={{ color: 'red' }}><strong>{parseJSON(cr.etat_caisse).reduce((s, l) => s + Number(l.sorties || 0), 0).toLocaleString('fr-FR')} FCFA</strong></td>
                  <td><strong>Solde : {(
                    parseJSON(cr.etat_caisse).reduce((s, l) => s + Number(l.entrees || 0), 0) -
                    parseJSON(cr.etat_caisse).reduce((s, l) => s + Number(l.sorties || 0), 0)
                  ).toLocaleString('fr-FR')} FCFA</strong></td>
                </tr>
              </tfoot>
            </table>
          </section>
        )}

        {/* DETTE TOTALE */}
        {Number(cr.dette_totale) > 0 && (
          <section className="section-dette">
            <h2>⚠️ Dette Totale de l'Association</h2>
            <p><strong style={{ color: 'red', fontSize: '1.2em' }}>
              {Number(cr.dette_totale).toLocaleString('fr-FR')} FCFA
            </strong></p>
          </section>
        )}

        {/* EMPRUNTS EN COURS */}
        {parseJSON(cr.emprunts_en_cours).length > 0 && (
          <section className="section-emprunts">
            <h2>🏦 Emprunts en Cours</h2>
            <table className="cotisations-table">
              <thead>
                <tr>
                  <th>Membre</th>
                  <th>Montant emprunté</th>
                  <th>Montant remboursé</th>
                  <th>Solde restant</th>
                </tr>
              </thead>
              <tbody>
                {parseJSON(cr.emprunts_en_cours).map((e, i) => (
                  <tr key={i}>
                    <td>{e.membre}</td>
                    <td>{Number(e.montant_session_24_25 || 0).toLocaleString('fr-FR')} FCFA</td>
                    <td>{Number(e.montant_session_25_26 || 0).toLocaleString('fr-FR')} FCFA</td>
                    <td><strong style={{ color: 'red' }}>{Number(e.total || 0).toLocaleString('fr-FR')} FCFA</strong></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td><strong>TOTAL</strong></td>
                  <td colSpan="2"></td>
                  <td><strong style={{ color: 'red' }}>
                    {parseJSON(cr.emprunts_en_cours).reduce((s, e) => s + Number(e.total || 0), 0).toLocaleString('fr-FR')} FCFA
                  </strong></td>
                </tr>
              </tfoot>
            </table>
          </section>
        )}

        {/* COMPTES BANCAIRES */}
        {parseJSON(cr.releve_bancaire).length > 0 && (
          <section className="section-comptes-bancaires">
            <h2>🏛️ Comptes Bancaires</h2>
            <table className="cotisations-table">
              <thead>
                <tr>
                  <th>Compte</th>
                  <th>Banque</th>
                  <th>Solde</th>
                </tr>
              </thead>
              <tbody>
                {parseJSON(cr.releve_bancaire).map((c, i) => (
                  <tr key={i}>
                    <td>{c.compte}</td>
                    <td>{c.banque}</td>
                    <td>{Number(c.solde || 0).toLocaleString('fr-FR')} FCFA</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="2"><strong>SOLDE BANCAIRE TOTAL</strong></td>
                  <td><strong style={{ color: 'green' }}>{Number(cr.solde_banque || 0).toLocaleString('fr-FR')} FCFA</strong></td>
                </tr>
              </tfoot>
            </table>
          </section>
        )}

        {/* ÉVÉNEMENT EN PRÉPARATION */}
        {parseJSON(cr.evenement_cotisations).length > 0 && (
          <section className="section-evenement">
            <h2>🎉 Événement{cr.evenement_nom ? ` — ${cr.evenement_nom}` : ''}</h2>
            <table className="cotisations-table">
              <thead>
                <tr>
                  <th>Membre</th>
                  <th>Montant attendu</th>
                  <th>Montant payé</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {parseJSON(cr.evenement_cotisations).map((l, i) => (
                  <tr key={i}>
                    <td>{l.membre}</td>
                    <td>{Number(l.montant_attendu || 0).toLocaleString('fr-FR')} FCFA</td>
                    <td>{Number(l.montant_paye || 0).toLocaleString('fr-FR')} FCFA</td>
                    <td>{l.statut}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td><strong>TOTAL</strong></td>
                  <td><strong>{parseJSON(cr.evenement_cotisations).reduce((s, l) => s + Number(l.montant_attendu || 0), 0).toLocaleString('fr-FR')} FCFA</strong></td>
                  <td><strong style={{ color: 'green' }}>{parseJSON(cr.evenement_cotisations).reduce((s, l) => s + Number(l.montant_paye || 0), 0).toLocaleString('fr-FR')} FCFA</strong></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </section>
        )}

        {/* ✅ POINTS DIVERS - CORRIGÉ avec parseJSON */}
        {parseJSON(cr.points_divers).length > 0 && (
          <section className="section-divers">
            <h2>💬 Points Divers</h2>
            {parseJSON(cr.points_divers).map((point, i) => (
              <div key={i} className="divers-item">
                <strong>{point.auteur}:</strong> {point.sujet}
                <p>{point.contenu}</p>
              </div>
            ))}
          </section>
        )}

        {/* ✅ PROCHAINES RÉUNIONS - CORRIGÉ avec parseJSON */}
        {parseJSON(cr.prochaines_reunions).length > 0 && (
          <section className="section-reunions">
            <h2>📅 Prochaines Réunions</h2>
            {parseJSON(cr.prochaines_reunions).map((reunion, i) => (
              <div key={i} className="reunion-item">
                <strong>{reunion.mois}</strong> - {reunion.date} à {reunion.heure}
                <p>{reunion.lieu}</p>
              </div>
            ))}
          </section>
        )}

        {cr.observations && (
          <section className="section-observations">
            <h2>📝 Observations</h2>
            <p>{cr.observations}</p>
          </section>
        )}
      </div>
      <div className="detail-actions">
        <button onClick={() => navigate(`/admin/comptes-rendus/modifier/${id}`)} className="btn-edit">
          ✏️ Modifier
        </button>
        
        <button 
          onClick={genererPDF} 
          className="btn-pdf"
          disabled={generationPDF.loading}
        >
          {generationPDF.loading ? '⏳ Génération...' : cr.pdf_filename ? '🔄 Régénérer PDF' : '📄 Générer PDF'}
        </button>
        
        {cr.pdf_url && (
          <a 
            href={cr.pdf_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="btn-download"
          >
            ⬇️ Télécharger PDF
          </a>
        )}
        
        {cr.statut === 'valide' && cr.pdf_url && (
          <button 
            onClick={envoyerParWhatsApp} 
            className="btn-whatsapp"
            disabled={envoi.loading}
          >
            {envoi.loading ? '⏳ Envoi en cours...' : '📱 Envoyer par WhatsApp'}
          </button>
        )}
        
        <button onClick={() => navigate('/admin/comptes-rendus')} className="btn-secondary">
          Retour à la liste
        </button>
      </div>

      {/* ✅ RÉSULTAT ENVOI - CORRIGÉ avec vérification details */}
      {envoi.resultat && (
        <div className="envoi-resultat">
          <h3>📊 Résultat de l'envoi</h3>
          <div className="resultat-stats">
            <div className="stat-success">✓ {envoi.resultat.succes} succès</div>
            {envoi.resultat.echecs > 0 && (
              <div className="stat-error">✗ {envoi.resultat.echecs} échecs</div>
            )}
          </div>
          
          {envoi.resultat.details && envoi.resultat.details.echecs && envoi.resultat.details.echecs.length > 0 && (
            <div className="echecs-details">
              <h4>Échecs d'envoi :</h4>
              <ul>
                {envoi.resultat.details.echecs.map((echec, i) => (
                  <li key={i}>
                    {echec.nom} ({echec.telephone}) : {echec.erreur}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CompteRenduDetail;